import React, { useState, useEffect, useMemo } from "react"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./quotes.scss"

// ChartJS imports
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title as ChartTitle, Tooltip, Legend } from "chart.js"
import { Bar } from "react-chartjs-2"

// UI components
import Tile from "../../components/structure/tile/tile"
import Table from "../../components/structure/table/table"
import Title from "../../components/structure/title/title"
import Select from "../../components/ui/select/select"
import Datepicker from "../../components/ui/datepicker/datepicker"
import QuoteRow from "./_components/quote"

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend)

// Functional component to return the quotes being generated
export default function AllQuotes() {
	const [loading, setLoading] = useState(false)
	const [quotes, setQuotes] = useState([])
	const [agents, setAgents] = useState({})
	const [clients, setClients] = useState({})
	const [selectedAgent, setSelectedAgent] = useState("")
	const [timeframe, setTimeframe] = useState("")
	const [customRange, setCustomRange] = useState(false)
	const [yearMonthMode, setYearMonthMode] = useState(false)
	const [selectedYear, setSelectedYear] = useState(moment().year())
	const [selectedMonth, setSelectedMonth] = useState(moment().month())
	const [startDate, setStartDate] = useState(null)
	const [endDate, setEndDate] = useState(null)

	// On component load, fetch all agents
	useEffect(() => {
		fetchAgents()
	}, [])

	// When filters change, fetch quotes (only if timeframe is selected)
	useEffect(() => {
		if (timeframe) {
			fetchQuotes()
		}
	}, [selectedAgent, timeframe, customRange, startDate, endDate, selectedYear, selectedMonth])

	// Fetch all agents from the database
	const fetchAgents = async () => {
		try {
			const agentsData = { "": "All Agents" }

			// Get all users ordered by first name (same as stats.js)
			const agentDocs = await db.collection("users").orderBy("first_name").get()

			// Loop through the agent documents found
			agentDocs.forEach((doc) => {
				const { first_name, last_name } = doc.data()
				agentsData[doc.id] = `${first_name} ${last_name}`
			})

			setAgents(agentsData)
		} catch (error) {
			console.error("Error fetching agents:", error)
		}
	}

	// Handle timeframe change
	const handleTimeframeChange = (option) => {
		const selectedOption = option.option
		setTimeframe(selectedOption)
		setCustomRange(selectedOption === "custom")
		setYearMonthMode(selectedOption === "year_month")

		// Update date range based on selected timeframe
		const now = moment()
		if (selectedOption === "week") {
			setStartDate(now.clone().subtract(1, "week").startOf("day").toDate())
			setEndDate(now.endOf("day").toDate())
		} else if (selectedOption === "month") {
			setStartDate(moment().subtract(1, "month").startOf("month").toDate())
			setEndDate(moment().subtract(1, "month").endOf("month").toDate())
		} else if (selectedOption === "year") {
			setStartDate(moment().subtract(1, "year").startOf("year").toDate())
			setEndDate(moment().subtract(1, "year").endOf("year").toDate())
		} else if (selectedOption === "year_month") {
			// Set to current year/month by default
			setSelectedYear(now.year())
			setSelectedMonth(now.month())
			setStartDate(moment().year(now.year()).month(now.month()).startOf("month").toDate())
			setEndDate(moment().year(now.year()).month(now.month()).endOf("month").toDate())
		}
	}

	// Handle year change for year/month mode
	const handleYearChange = (option) => {
		const year = parseInt(option.option)
		setSelectedYear(year)
		setStartDate(moment().year(year).month(selectedMonth).startOf("month").toDate())
		setEndDate(moment().year(year).month(selectedMonth).endOf("month").toDate())
	}

	// Handle month change for year/month mode
	const handleMonthChange = (option) => {
		const month = parseInt(option.option)
		setSelectedMonth(month)
		setStartDate(moment().year(selectedYear).month(month).startOf("month").toDate())
		setEndDate(moment().year(selectedYear).month(month).endOf("month").toDate())
	}

	// Handle date changes for custom range
	const handleStartDateChange = (milliseconds) => {
		setStartDate(moment(milliseconds).toDate())
	}

	const handleEndDateChange = (milliseconds) => {
		setEndDate(moment(milliseconds).toDate())
	}

	// Compute chart data from quotes
	const chartData = useMemo(() => {
		if (!quotes.length) return null

		// Group quotes by date
		const quotesPerDay = {}
		quotes.forEach((quote) => {
			const date = moment(quote.created?.seconds, "X").format("MMM D")
			quotesPerDay[date] = (quotesPerDay[date] || 0) + 1
		})

		// Sort dates chronologically
		const sortedDates = Object.keys(quotesPerDay).sort((a, b) => {
			return moment(a, "MMM D").valueOf() - moment(b, "MMM D").valueOf()
		})

		return {
			labels: sortedDates,
			datasets: [
				{
					label: "Quotes",
					data: sortedDates.map((date) => quotesPerDay[date]),
					backgroundColor: "rgba(59, 130, 246, 0.7)",
					borderColor: "rgba(59, 130, 246, 1)",
					borderWidth: 1,
					borderRadius: 4,
				},
			],
		}
	}, [quotes])

	// Chart options
	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				display: false,
			},
			title: {
				display: true,
				text: "Quotes per Day",
				font: {
					size: 16,
					weight: "600",
				},
			},
		},
		scales: {
			y: {
				beginAtZero: true,
				ticks: {
					stepSize: 1,
				},
			},
		},
	}

	// Get timeframe display text
	const getTimeframeText = () => {
		if (!timeframe) {
			return "Select timeframe"
		}

		if (customRange && startDate && endDate) {
			return `${moment(startDate).format("MMM D, YYYY")} - ${moment(endDate).format("MMM D, YYYY")}`
		}

		if (yearMonthMode) {
			return `${moment().month(selectedMonth).format("MMMM")} ${selectedYear}`
		}

		const timeframeOptions = {
			week: "Last Week",
			month: "Last Month",
			year: "Last Year",
			custom: "Custom Range",
			year_month: "Select Month",
		}

		return timeframeOptions[timeframe] || "Select timeframe"
	}

	// Fetch quotes with filters
	const fetchQuotes = async () => {
		// Don't fetch if custom range is selected but dates aren't set
		if (customRange && (!startDate || !endDate)) {
			return
		}

		setLoading(true)

		try {
			// Calculate date range
			let calculatedStartDate, calculatedEndDate

			if (customRange) {
				calculatedStartDate = moment(startDate).startOf("day")
				calculatedEndDate = moment(endDate).endOf("day")
			} else if (yearMonthMode) {
				calculatedStartDate = moment().year(selectedYear).month(selectedMonth).startOf("month")
				calculatedEndDate = moment().year(selectedYear).month(selectedMonth).endOf("month")
			} else {
				const now = moment()
				if (timeframe === "week") {
					calculatedStartDate = now.clone().subtract(1, "week").startOf("day")
					calculatedEndDate = now.endOf("day")
				} else if (timeframe === "month") {
					calculatedStartDate = now.clone().subtract(1, "month").startOf("month")
					calculatedEndDate = now.clone().subtract(1, "month").endOf("month")
				} else if (timeframe === "year") {
					calculatedStartDate = now.clone().subtract(1, "year").startOf("year")
					calculatedEndDate = now.clone().subtract(1, "year").endOf("year")
				}
			}

			// Convert to Firebase timestamps
			const startTimestamp = firebase.firestore.Timestamp.fromMillis(calculatedStartDate.valueOf())
			const endTimestamp = firebase.firestore.Timestamp.fromMillis(calculatedEndDate.valueOf())

			// Build query - only apply limit if no agent selected
			let query
			if (selectedAgent) {
				// No limit when agent is selected
				query = db.collection("quotes")
					.where("agent", "==", selectedAgent)
					.where("created", ">=", startTimestamp)
					.where("created", "<=", endTimestamp)
					.orderBy("created", "desc")
			} else {
				// Apply limit when showing all agents
				query = db.collection("quotes")
					.where("created", ">=", startTimestamp)
					.where("created", "<=", endTimestamp)
					.orderBy("created", "desc")
					.limit(200)
			}

			const querySnapshot = await query.get()
			const quotesData = []
			const clientIds = new Set()

			querySnapshot.forEach((doc) => {
				const data = doc.data()
				quotesData.push({ id: doc.id, ...data })
				// Collect unique client IDs
				if (data.client) {
					clientIds.add(data.client)
				}
			})

			// Batch fetch client names
			if (clientIds.size > 0) {
				const clientsData = {}
				const clientIdsArray = Array.from(clientIds)
				
				// Firestore 'in' queries are limited to 10 items, so batch them
				const batchSize = 10
				for (let i = 0; i < clientIdsArray.length; i += batchSize) {
					const batch = clientIdsArray.slice(i, i + batchSize)
					const clientDocs = await db.collection("clients")
						.where(firebase.firestore.FieldPath.documentId(), "in", batch)
						.get()
					
					clientDocs.forEach((doc) => {
						const { first_name, last_name } = doc.data()
						clientsData[doc.id] = `${first_name} ${last_name}`
					})
				}
				
				setClients(clientsData)
			}

			setQuotes(quotesData)
			setLoading(false)
		} catch (error) {
			console.error("Error fetching quotes:", error)
			setLoading(false)
		}
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>All Quotes</h1>
			</Title>

			<div className="quotes-controls">
				<div className="select-wrapper">
					<label>Agent:</label>
					<Select
						placeholder="All Agents"
						value={selectedAgent ? agents[selectedAgent] : "All Agents"}
						onSelect={(option) => setSelectedAgent(option.option)}
						options={agents}
					/>
				</div>

				<div className="select-wrapper">
					<label>Timeframe:</label>
					<Select
						placeholder="Select timeframe"
						value={getTimeframeText()}
						onSelect={handleTimeframeChange}
						options={{
							year_month: "Select Month",
							week: "Last Week",
							month: "Last Month",
							year: "Last Year",
							custom: "Custom Range",
						}}
					/>
				</div>
			</div>

			{customRange && (
				<div className="date-range-controls">
					<div className="date-picker-wrapper">
						<label>Start Date:</label>
						<Datepicker
							placeholder="Start Date"
							value={moment(startDate).valueOf()}
							onSelect={handleStartDateChange}
						/>
					</div>
					<div className="date-picker-wrapper">
						<label>End Date:</label>
						<Datepicker
							placeholder="End Date"
							value={moment(endDate).valueOf()}
							onSelect={handleEndDateChange}
						/>
					</div>
				</div>
			)}

			{yearMonthMode && !customRange && (
				<div className="date-range-controls">
					<div className="date-picker-wrapper">
						<label>Year:</label>
						<Select
							placeholder="Select Year"
							value={selectedYear.toString()}
							onSelect={handleYearChange}
							options={Object.fromEntries(
								Array.from({ length: 5 }, (_, i) => {
									const year = moment().year() - i
									return [year.toString(), year.toString()]
								})
							)}
						/>
					</div>
					<div className="date-picker-wrapper">
						<label>Month:</label>
						<Select
							placeholder="Select Month"
							value={moment().month(selectedMonth).format("MMMM")}
							onSelect={handleMonthChange}
							options={Object.fromEntries(Array.from({ length: 12 }, (_, i) => [i.toString(), moment().month(i).format("MMMM")]))}
						/>
					</div>
				</div>
			)}

			{!loading && timeframe && !selectedAgent && quotes.length === 200 && (
				<div className="quotes-limit-notice">
					Results are limited to the first 200 quotes. Select a specific agent to view all quotes.
				</div>
			)}

			{!loading && timeframe && quotes.length > 0 && chartData && (
				<div className="quotes-chart">
					<Bar data={chartData} options={chartOptions} />
				</div>
			)}

			{!loading && timeframe && (
				<div className="quotes-count">
					<p><strong>{quotes.length}</strong> quote{quotes.length !== 1 ? 's' : ''} found</p>
				</div>
			)}

			<Table
				className="enquiries-table"
				headings={["Client Name", "Agent", "Created"]}>
				{!timeframe && (
					<tr>
						<td colSpan="8">Please select a timeframe to view quotes.</td>
					</tr>
				)}

				{timeframe && loading && (
					<tr>
						<td colSpan="8">Loading...</td>
					</tr>
				)}

				{timeframe && !loading && quotes.length === 0 && (
					<tr>
						<td colSpan="8">No quotes found for the selected filters.</td>
					</tr>
				)}

				{timeframe && !loading &&
					quotes.map((quote, index) => (
						<QuoteRow
							key={index}
							quote={quote}
							clientName={clients[quote.client] || "Loading..."}
							agentName={agents[quote.agent] || "Loading..."}
						/>
					))}
			</Table>
		</Tile>
	)
}
